import { Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { BalanceService } from 'src/app/api/balance.service';
import { Balance, CurrencyBalance } from 'src/app/models/balance';
import { Currency, CurrencyType } from 'src/app/models/currency';
import { ProfileService } from 'src/app/api/profile.service';
import { PromotionsService } from 'src/app/api/promotions.service';
import { PromotionStatus, VerifyAndEarnPromotion } from 'src/app/models/promotions';
import { ExcahngeRate } from 'src/app/models/exchange-rate';
import { environment } from 'src/environments/environment';

enum TitleType {
  Code = 'code',
  Name = 'name',
  All = 'all'
}

@Component({
  selector: 'wallet-balance',
  templateUrl: './wallet-balance.component.html',
  styleUrls: ['./wallet-balance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WalletBalanceComponent implements OnInit, OnDestroy, OnChanges {
  @Input() type: CurrencyType;
  @Input() code: string;
  @Input() title: TitleType = TitleType.Name;
  @Input() containerHeight: number;

  BTC_WALLET = 'BTC-WALLET';
  TitleType = TitleType;
  PromotionStatus = PromotionStatus;
  CurrencyType = CurrencyType;

  allowWallet = true;

  data: CurrencyBalance[];
  error: string;

  walletData: CurrencyBalance;
  filteredData: CurrencyBalance[];

  total: ExcahngeRate;
  globalCurrencyTotal: ExcahngeRate;
  baseCurrencyTotal: ExcahngeRate;

  promotion: VerifyAndEarnPromotion;

  balanceRefreshSubscription: Subscription;

  globalCurrency = environment.currencies.globalCurrency;
  defaultCurrency: string;
  useGlobalCurrency = !!this.globalCurrency;

  constructor(
    private profileService: ProfileService,
    private balanceService: BalanceService,
    private promotionsService: PromotionsService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.getProfileData();
    this.getPromotions();
    this.subscribeToBalanceChanges();
  }

  ngOnDestroy(): void {
    this.balanceRefreshSubscription?.unsubscribe();
  }

  subscribeToBalanceChanges() {
    this.balanceRefreshSubscription?.unsubscribe();
    this.balanceRefreshSubscription = this.balanceService.refreshChanges.subscribe(_ => this.getBalanceData());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['code'] && changes['code'].currentValue !== changes['code'].previousValue && !changes['code'].isFirstChange()) {
      this.filterResults();
    }
  }

  getProfileData() {
    this.profileService.get().subscribe(response => {
      this.allowWallet = response?.multisigBtcWalletEnabled;
      this.defaultCurrency = response?.preferences?.fiatCurrencyCode;
      this.getBalanceData();
    }, err => {
      this.allowWallet = true;
      this.defaultCurrency = environment.currencies.defaultCurrency;
    });
  }

  getBalanceData() {
    this.balanceService.getAll().subscribe(response => {
      if (response.wallet && response.wallet.currency) {
        response.wallet.currency.name = this.BTC_WALLET;
      }

      if (this.allowWallet) {
        this.data = this.balanceService.sortByValue([response.wallet, ...response.exchange]);
      } else {
        this.data = this.balanceService.sortByValue(response.exchange);
      }

      this.filterResults();
      this.calculateTotal();
      this.changeDetectorRef.detectChanges();
    }, err => {
      this.error = err.errors[0].message;
    });
  }

  getPromotions() {
    this.promotionsService.verifyAndEarn().subscribe(response => {
      this.promotion = response;
      this.changeDetectorRef.detectChanges();
    }, err => {});
  }

  filterResults() {
    if (!this.data) { return; }

    if (this.code === this.BTC_WALLET) {
      this.filteredData = [this.data.find(item => item.currency.name === this.BTC_WALLET)];
    } else if (this.code) {
      this.filteredData = [this.data.find(item => item.currency.code === this.code.toUpperCase())];
    } else if (this.type) {
      this.filteredData = this.data.filter(item => item.currency.type === this.type);
    } else {
      this.filteredData = this.data;
    }
  }

  calculateTotal() {
    let total = 0;
    let usdTotal = 0;

    this.data?.forEach(item => {
      total += parseFloat(item?.balance?.value?.amount || '0');
      usdTotal += parseFloat(item?.balance?.usdValue?.amount || '0');
    });

    this.baseCurrencyTotal = {
      amount: total,
      currency: this?.data?.[0].balance?.value?.currency
    };

    this.globalCurrencyTotal = {
      amount: usdTotal,
      currency: this?.data?.[0].balance?.usdValue?.currency
    };

    this.total = this.useGlobalCurrency ? this.globalCurrencyTotal : this.baseCurrencyTotal;
  }

  isFiatCurrency(currency: Currency) {
    return currency.type === CurrencyType.Fiat;
  }
}
